import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RevealText } from './common/RevealText';
import Container from './common/Container';
import { Plus, Minus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const faqs = [
    {
        question: "What type of information can I find on this website?",
        answer: "You can explore our comprehensive portfolio of premium residential and commercial real estate developments. We provide detailed project insights, including locations, modern amenities, architectural highlights, and image galleries for our ongoing, upcoming, and completed projects."
    },
    {
        question: "What differentiates Valletta Group from other developers?",
        answer: "We prioritize construction quality, transparent dealings, and timely delivery. As proud members of CREDAI SURAT and SEBA, our track record of over 5+ completed projects and 1.46M+ Sq. Ft. of delivered space speaks to our unwavering commitment to architectural excellence."
    },
    {
        question: "Are your projects fully RERA registered?",
        answer: "Absolutely. All Valletta Group projects are fully RERA registered and strictly comply with all local building regulations and ethical pricing norms to ensure complete peace of mind and transparency."
    },
    {
        question: "How can I request project brochures or floor plans?",
        answer: "Basic project overviews and gallery images are available directly on our project pages. For comprehensive digital brochures, detailed floor plans, or specific availability inquiries, please reach out to our team via the 'Contact Us' page."
    },
    {
        question: "What kind of amenities are typically included in your projects?",
        answer: "While amenities vary by the specific development, our premium residential projects typically feature well-equipped gymnasiums, landscaped gardens, dedicated parking spaces, children's play areas, and robust 24/7 security systems."
    },
    {
        question: "Can I schedule a secure site visit through the website?",
        answer: "Yes, we highly encourage prospective buyers to visit our sites to experience our construction quality firsthand. You can easily schedule a guided site visit by sending an inquiry through the contact form on any project page."
    }
];

const FAQ = () => {
    const [activeIndex, setActiveIndex] = useState(0);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <section className="py-20 lg:py-28 bg-white relative overflow-hidden">
            {/* Background Accent */}
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary/5 blur-[120px] rounded-full pointer-events-none" />

            <Container className="relative z-10  mx-auto">
                <div className="flex flex-col items-center mb-16 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-secondary font-bold tracking-[0.15em] uppercase text-xs block mb-4"
                    >
                        SUPPORT
                    </motion.span>
                    <h2 className="text-4xl lg:text-5xl font-serif text-primary font-bold leading-tight mb-6">
                        <RevealText text="Frequently Asked Questions" className='py-3 md:pb-5' />
                    </h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-secondary text-base md:text-lg font-sans leading-relaxed max-w-2xl font-medium"
                    >
                        Everything you need to know about our projects, services, and policies. Can't find the answer you're looking for? Contact our support team.
                    </motion.p>
                </div>

                {/* Accordion List */}
                <div className="w-full">
                    <div className="border-t border-secondary/20">
                        {faqs.map((faq, index) => {
                            const isOpen = activeIndex === index;
                            return (
                                <motion.div
                                    key={index}
                                    className="border-b border-secondary/20 py-5 lg:py-6 group"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <button
                                        onClick={() => toggleFAQ(index)}
                                        className="w-full flex items-start justify-between text-left focus:outline-none gap-6"
                                    >
                                        <span className={`text-lg md:text-xl font-medium font-sans transition-colors duration-300 ${isOpen ? 'text-primary' : 'text-primary/90 group-hover:text-secondary'}`}>
                                            {faq.question}
                                        </span>
                                        <div className={`shrink-0 mt-1 w-[22px] h-[22px] rounded-full border flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-primary/90 text-white border-secondary' : 'text-secondary border-secondary/40 group-hover:border-secondary'}`}>
                                            {isOpen ? <Minus size={12} strokeWidth={2.5} /> : <Plus size={12} strokeWidth={2.5} />}
                                        </div>
                                    </button>
                                    <AnimatePresence>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                                className="overflow-hidden"
                                            >
                                                <div className="pt-4 pb-2 text-secondary text-md md:text-lg font-sans leading-[1.8] pr-12">
                                                    {faq.answer}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </Container>
        </section>
    );
};

export default FAQ;
